import { DOCUMENT, Location, isPlatformBrowser } from '@angular/common';
import {
  Inject,
  Injectable,
  InjectionToken,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  Renderer2,
  RendererFactory2,
  RendererStyleFlags2,
} from '@angular/core';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { Keyboard } from '@capacitor/keyboard';
import { Network } from '@capacitor/network';
import { PushNotifications } from '@capacitor/push-notifications';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { WINDOW } from '@ng-web-apis/common';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { NavigationService } from '../navigation/navigation.service';
import { OpenSnackBarService } from '../open-snack-bar/open-snack-bar.service';
import { UserService } from '../user/user.service';
import { baseUrl, navigateToExternalUrl, openNativeAlertDialog, openNativeSettings } from '../utils/helpers';
const CapacitorUpdaterWithCheck = CapacitorUpdater as typeof CapacitorUpdater & {
  checkUpdate: () => Promise<void>;
};
@Injectable()
export class CapacitorService implements OnDestroy {
  private renderer: Renderer2;
  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(WINDOW) private window: Window,
    @Inject(I18NEXT_SERVICE) private translateService: ITranslationService,
    @Inject(PLATFORM_ID) private platformId: NonNullable<unknown>,
    private location: Location,
    private navigationService: NavigationService,
    private openSnackBarService: OpenSnackBarService,
    private router: Router,
    private ngZone: NgZone,
    private userService: UserService,
    private rendererFactory2: RendererFactory2,
  ) {
    this.renderer = this.rendererFactory2.createRenderer(null, null);
  }
  init() {
    if (isPlatformBrowser(this.platformId)) {
      Keyboard.addListener('keyboardWillShow', info => {
        if (Capacitor.getPlatform() === 'ios') {
          this.renderer.setStyle(
            this.document.querySelector('.app-wrapper'),
            'padding-bottom',
            info.keyboardHeight + 'px',
            RendererStyleFlags2.Important,
          );
          if (this.document.querySelector('.generic-dialog__content')) {
            this.renderer.setStyle(
              this.document.querySelector('.generic-dialog__content'),
              'padding-bottom',
              info.keyboardHeight + 'px',
              RendererStyleFlags2.Important,
            );
          }
          if (this.document.querySelector('.create__wrapper')) {
            this.renderer.removeStyle(this.document.querySelector('.app-wrapper'), 'padding-bottom');
            this.renderer.setStyle(
              this.document.querySelector('.create__wrapper'),
              'padding-bottom',
              info.keyboardHeight + 'px',
              RendererStyleFlags2.Important,
            );
          }
          if (this.document.querySelector('.dialog-mobile__body')) {
            this.renderer.setStyle(
              this.document.querySelector('.dialog-mobile__body'),
              'padding-bottom',
              info.keyboardHeight + 'px',
              RendererStyleFlags2.Important,
            );
          }
        }
        if (this.document.querySelector('.messages__conversation--with-id')) {
          this.document.querySelector('.app-wrapper').scrollBy(0, info.keyboardHeight);
          if (Capacitor.getPlatform() === 'ios') {
            this.renderer.addClass(
              this.document.querySelector('.messages__conversation'),
              'messages__conversation--keyboard',
            );
            this.renderer.setStyle(
              this.document.querySelector('.sending-box-wrapper'),
              'bottom',
              info.keyboardHeight + 'px',
            );
          }
        }
      });

      Keyboard.addListener('keyboardWillHide', () => {
        if (Capacitor.getPlatform() === 'ios') {
          this.renderer.removeStyle(this.document.querySelector('.app-wrapper'), 'padding-bottom');
          if (this.document.querySelector('.generic-dialog__content')) {
            this.renderer.removeStyle(this.document.querySelector('.generic-dialog__content'), 'padding-bottom');
          }
          if (this.document.querySelector('.create__wrapper')) {
            this.renderer.removeStyle(this.document.querySelector('.create__wrapper'), 'padding-bottom');
          }
          if (this.document.querySelector('.messages__conversation--with-id')) {
            this.renderer.removeClass(
              this.document.querySelector('.messages__conversation'),
              'messages__conversation--keyboard',
            );
            this.renderer.removeStyle(this.document.querySelector('.sending-box-wrapper'), 'bottom');
          }
        }
      });

      CapacitorUpdater.addListener('appReloaded', () =>
        CapacitorUpdater.notifyAppReady().catch(error => console.error(error)),
      );
      CapacitorUpdater.notifyAppReady().catch(error => console.error(error));
      CapacitorUpdater.setMultiDelay({
        delayConditions: [{ kind: 'kill' }, { kind: 'background', value: '120000' }],
      }).catch(error => console.error(error));
      if (Capacitor.getPlatform() === 'android') {
        CapacitorUpdaterWithCheck.checkUpdate()
          .then(() => console.debug('Update check complete'))
          .catch(err => console.error('[CapacitorUpdater] checkUpdate failed:', err));
      }
      App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
        this.window.requestAnimationFrame(() => {
          this.ngZone.run(() => {
            if (this.userService.user) {
              const slug = event.url.split(baseUrl.replace('https://', '').replace('www.', '')).pop();
              const slugLang = slug.split('/')[1];
              const decodedUrl = decodeURIComponent(slug);
              if (slug) {
                Browser.close();
                this.location.replaceState(baseUrl + decodedUrl);
                const langPromise = new Promise<void>(resolve => {
                  if (['it', 'en', 'es', 'fr', 'de'].includes(slugLang)) {
                    this.translateService.changeLanguage(slugLang).then(() => resolve());
                  } else {
                    resolve();
                  }
                });
                langPromise.then(() =>
                  this.router
                    .navigateByUrl(decodedUrl, { skipLocationChange: false, state: { refresh: true } })
                    .then(v => {
                      console.log(v);
                    })
                    .catch(e => console.log(e)),
                );
              }
            } else {
              navigateToExternalUrl(this.window, event.url, this.openSnackBarService, this.translateService);
            }
          });
        });
      });
      App.addListener('backButton', () => {
        if (this.navigationService.previousUrls?.length > 1) {
          this.navigationService.back();
        } else {
          App.exitApp();
        }
      });
      App.addListener('appStateChange', appState => {
        if (appState.isActive) {
          CapacitorUpdater.notifyAppReady().catch(error => console.error(error));
        }
      });

      Geolocation.checkPermissions()
        .then(permission => {
          if (permission.coarseLocation === 'denied' && permission.location === 'denied') {
            if (!!this.userService.user && !this.userService.user.refusedGeolocation) {
              openNativeAlertDialog(
                this.translateService.t('capacitor.dialog.permission-denied.geolocation.title'),
                this.translateService.t('capacitor.dialog.permission-denied.geolocation.message'),
                this.translateService.t('capacitor.dialog.permission-denied.confirm-button'),
                this.translateService.t('capacitor.dialog.permission-denied.cancel-button'),
              )
                .then(result => {
                  if (result.value) {
                    openNativeSettings();
                  }
                })
                .catch(error => console.error(error));
            }
          } else {
            Geolocation.requestPermissions({ permissions: ['coarseLocation', 'location'] })
              .then(permissionStatus => {
                if (permissionStatus.coarseLocation === 'granted' || permissionStatus.location === 'granted') {
                  this.userService.setUserRefusedGeolocation(false);
                } else {
                  this.userService.setUserRefusedGeolocation(true);
                }
              })
              .catch(error => console.error(error));
          }
        })
        .catch(error => console.error(error));
    }
  }
  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      Network.removeAllListeners()
        .then(() => console.log('all network listener removed'))
        .catch(error => console.error(error));
      Keyboard.removeAllListeners()
        .then(() => console.log('all keyboard listener removed'))
        .catch(error => console.error(error));
      App.removeAllListeners()
        .then(() => console.log('all app listener removed'))
        .catch(error => console.error(error));
      PushNotifications.removeAllListeners()
        .then(() => console.log('all pushNotification listener removed'))
        .catch(error => console.error(error));
    }
  }
}

export const CAPACITOR_SERVICE = new InjectionToken<CapacitorService>('capacitor');
